/**
 * @description E5 result classifier.
 * Turns a serviceProvider result into an outcome for Zone / Network / Mapping screens,
 * so screens never match message text themselves.
 *
 * The HTTP status decides the family (409 conflict, 404 not found, 400 validation).
 * The backend ProblemDetails has no `errors` field, so the specific outcome and the
 * form field come from an exact match on `detail` (or on `message` for endpoints
 * still on the 200 envelope). Texts below are copied from the BE CommonConstant.cs
 * on feature/e5-config-crud-fix; if a text changes there, change it here.
 */

// outcome values
export const OUTCOME = {
  SUCCESS: "success",
  DUPLICATE: "duplicate",
  IMMUTABLE: "immutable",
  ALREADY_IN_STATE: "alreadyInState",
  NOT_FOUND: "notFound",
  CONFLICT: "conflict",
  VALIDATION: "validation",
  AUTH: "auth",
  TIMEOUT: "timeout",
  NETWORK: "network",
  SERVER: "server",
  UNKNOWN: "unknown",
};

// fallback texts when the backend sends none (placeholders until Adam approves copy)
export const DEFAULT_TEXT = {
  conflict: "This conflicts with an existing record.",
  notFound: "This record no longer exists.",
  validation: "Please correct the highlighted fields.",
  auth: "Your session has expired. Please log in again.",
  timeout:
    "The server is taking too long. Please refresh to check whether the change was saved.",
  network: "Unable to reach the server. Please check your connection.",
  server: "Something went wrong, please try after sometime.",
};

/**
 * user-facing copy from the E5 design (shown instead of the backend text)
 */
export const DESIGN_TEXT = {
  generic: "Something went wrong. Please try again.",
  notSaved: "Your changes were not saved.",
  numberLocked: "Number can't be changed after creation.",
  zoneCreated: "Zone created successfully",
  zoneUpdated: "Zone updated successfully",
  networkCreated: "Network created successfully",
  networkUpdated: "Network updated successfully",
  // Option B: number + name matched an existing network, which was mapped to this zone
  // (Media URL is per zone mapping, so the typed URL is saved for this zone)
  networkAttached: "Existing Network attached to this Zone.",
  zoneDeactivated: "Zone deactivated",
  mappingDeactivated: "Mapping deactivated",
  mappingDuplicate: "This Network is already mapped to this Zone.",
};

// inline 409 duplicate messages per entity and field (design)
export const DUPLICATE_TEXT = {
  zone: {
    zoneName: "A Zone with this name already exists.",
    zoneNumber: "A Zone with this number already exists.",
  },
  // Option B: a network is unique across all zones, so a clash means the other field differs
  network: {
    networkName: "This Network Name is already used by a network with a different number.",
    networkNumber: "This Network Number is already used by a network with a different name.",
  },
};

const entry = (outcome, entity, fields, extra) => ({
  outcome,
  entity,
  fields: fields || [],
  ...(extra || {}),
});

// exact backend texts -> outcome, entity and the Formik fields to highlight
const MESSAGE_MAP = {
  // success
  "Zone created successfully.": entry(OUTCOME.SUCCESS, "zone"),
  "Zone updated successfully.": entry(OUTCOME.SUCCESS, "zone"),
  "Zone deactivated successfully.": entry(OUTCOME.SUCCESS, "zone"),
  "Zone reactivated successfully.": entry(OUTCOME.SUCCESS, "zone"),
  "Network created successfully.": entry(OUTCOME.SUCCESS, "network"),
  "Network modified successfully.": entry(OUTCOME.SUCCESS, "network"),
  "Existing Network attached to this Zone.": entry(OUTCOME.SUCCESS, "network", [], { action: "attached" }),
  "Network deactivated successfully.": entry(OUTCOME.SUCCESS, "network"),
  "Network reactivated successfully.": entry(OUTCOME.SUCCESS, "network"),
  "Zone-Network mapping deactivated successfully.": entry(OUTCOME.SUCCESS, "mapping"),
  "Zone-Network mapping reactivated successfully.": entry(OUTCOME.SUCCESS, "mapping"),

  // duplicate on create / update
  "Zone number in use. Please enter unique zone number": entry(OUTCOME.DUPLICATE, "zone", ["zoneNumber"]),
  "Zone Name already present": entry(OUTCOME.DUPLICATE, "zone", ["zoneName"]),
  "Network number in use. Please enter unique network number": entry(OUTCOME.DUPLICATE, "network", ["networkNumber"]),
  "Network Name already present": entry(OUTCOME.DUPLICATE, "network", ["networkName"]),
  "Network already mapped with zone.": entry(OUTCOME.DUPLICATE, "mapping", ["networkName", "networkNumber"]),

  // number changed on update
  "Zone number cannot be changed after creation.": entry(OUTCOME.IMMUTABLE, "zone", ["zoneNumber"]),
  "Network number cannot be changed after creation.": entry(OUTCOME.IMMUTABLE, "network", ["networkNumber"]),

  // already in the target state
  "This Zone is already deactivated.": entry(OUTCOME.ALREADY_IN_STATE, "zone"),
  "This Zone is already active.": entry(OUTCOME.ALREADY_IN_STATE, "zone"),
  "This Network is already deactivated.": entry(OUTCOME.ALREADY_IN_STATE, "network"),
  "This Network is already active.": entry(OUTCOME.ALREADY_IN_STATE, "network"),
  "This Zone-Network mapping is already deactivated.": entry(OUTCOME.ALREADY_IN_STATE, "mapping"),
  "This Zone-Network mapping is already active.": entry(OUTCOME.ALREADY_IN_STATE, "mapping"),

  // not found
  "Zone not found.": entry(OUTCOME.NOT_FOUND, "zone"),
  "Network not found.": entry(OUTCOME.NOT_FOUND, "network"),
  "Zone-Network mapping not found.": entry(OUTCOME.NOT_FOUND, "mapping"),

  // reactivate collision
  "Cannot reactivate: name or number is now in use by another active Zone.": entry(OUTCOME.CONFLICT, "zone"),
  "Cannot reactivate: name or number is now in use by another active Network.": entry(OUTCOME.CONFLICT, "network"),
  "Cannot reactivate: this Zone-Network pair is already mapped by another active mapping.": entry(OUTCOME.CONFLICT, "mapping"),
};

// trim + case-insensitive lookup (exact text, not substring)
const normalize = (text) => (typeof text === "string" ? text.trim().toLowerCase() : "");
const LOOKUP = Object.keys(MESSAGE_MAP).reduce((map, key) => {
  map[normalize(key)] = MESSAGE_MAP[key];
  return map;
}, {});

const lookup = (text) => LOOKUP[normalize(text)];

/**
 * @param {*serviceProvider result} result
 * @returns {outcome, entity, fields, fieldErrors, text}
 * text is what to show the user; fields are the Formik fields to highlight
 */
export const classify = (result) => {
  let res = result || {};
  let kind = res.kind;
  let base = { outcome: OUTCOME.UNKNOWN, entity: "", fields: [], fieldErrors: {}, text: "" };

  // 409 / 404: backend text is in detail; field conflicts also carry an `errors` map (BE 5b9e187)
  if (kind === "conflict" || kind === "notFound") {
    let text = res.detail || res.title || "";
    let found = lookup(text);
    let mapFields = Object.keys(res.fieldErrors || {});
    if (kind === "conflict" && mapFields.length > 0) {
      // the errors map names the field; the detail text (if known) gives the outcome
      let entity = found ? found.entity : mapFields[0].startsWith("network") ? "network" : "zone";
      let outcome =
        found && (found.outcome === OUTCOME.DUPLICATE || found.outcome === OUTCOME.IMMUTABLE)
          ? found.outcome
          : OUTCOME.DUPLICATE;
      return { ...base, outcome, entity, fields: mapFields, text: text.trim() };
    }
    if (found) {
      return { ...base, ...found, text: text.trim() };
    }
    let outcome = kind === "notFound" ? OUTCOME.NOT_FOUND : OUTCOME.CONFLICT;
    return { ...base, outcome, text: text || DEFAULT_TEXT[kind] };
  }

  // 400: field errors from ValidationProblemDetails
  if (kind === "validation") {
    let fieldErrors = res.fieldErrors || {};
    return {
      ...base,
      outcome: OUTCOME.VALIDATION,
      fields: Object.keys(fieldErrors),
      fieldErrors,
      text: DEFAULT_TEXT.validation,
    };
  }

  if (kind === "auth" || kind === "forbidden") {
    return { ...base, outcome: OUTCOME.AUTH, text: DEFAULT_TEXT.auth };
  }
  if (kind === "timeout") {
    return { ...base, outcome: OUTCOME.TIMEOUT, text: DEFAULT_TEXT.timeout };
  }
  if (kind === "network") {
    return { ...base, outcome: OUTCOME.NETWORK, text: DEFAULT_TEXT.network };
  }
  if (kind === "server") {
    return { ...base, outcome: OUTCOME.SERVER, text: res.detail || DEFAULT_TEXT.server };
  }

  // 200 envelope: body httpStatusCode 500 is a server error
  if (kind === "business" && res.status >= 500) {
    return { ...base, outcome: OUTCOME.SERVER, text: res.message || DEFAULT_TEXT.server };
  }

  // 200 envelope (success, or Network / Mapping endpoints still returning results as message)
  if (kind === "success" || kind === "business") {
    let text = res.message || "";
    let found = lookup(text);
    if (found) {
      return { ...base, ...found, text: text.trim() };
    }
    return { ...base, text: text || DEFAULT_TEXT.server };
  }

  return { ...base, text: DEFAULT_TEXT.server };
};

/**
 * @description what a Zone / Network form shows after Save, per the E5 design
 * @param {*classify() result} r
 * @param {*names of the fields on the form; 400 errors for other fields fall back to formError + toast} formFields
 * @returns {ok, fieldErrors, formError, toast}
 *  fieldErrors: inline under the field (409 duplicate / immutable, 400 validation)
 *  formError: message beside Save (mapping duplicate, or "Your changes were not saved.")
 *  toast: error toast text, or "" for none
 */
export const formFeedback = (r, formFields) => {
  let res = r || {};
  if (res.outcome === OUTCOME.SUCCESS) {
    return { ok: true, action: res.action || "", fieldErrors: {}, formError: "", toast: "" };
  }
  if (res.outcome === OUTCOME.DUPLICATE) {
    if (res.entity === "mapping") {
      return { ok: false, fieldErrors: {}, formError: DESIGN_TEXT.mappingDuplicate, toast: "" };
    }
    let copy = DUPLICATE_TEXT[res.entity] || {};
    let fieldErrors = {};
    res.fields.forEach((f) => {
      fieldErrors[f] = copy[f] || res.text;
    });
    return { ok: false, fieldErrors, formError: "", toast: "" };
  }
  if (res.outcome === OUTCOME.IMMUTABLE) {
    let fieldErrors = {};
    res.fields.forEach((f) => {
      fieldErrors[f] = DESIGN_TEXT.numberLocked;
    });
    return { ok: false, fieldErrors, formError: "", toast: "" };
  }
  if (res.outcome === OUTCOME.VALIDATION) {
    // only errors the form can show next to a field; e.g. "Zone.ZoneName" on the network form has no field
    let fieldErrors = {};
    Object.keys(res.fieldErrors || {}).forEach((f) => {
      if (!formFields || formFields.includes(f)) fieldErrors[f] = res.fieldErrors[f];
    });
    if (Object.keys(fieldErrors).length > 0) {
      return { ok: false, fieldErrors, formError: "", toast: "" };
    }
  }
  // no field to blame: toast + "Your changes were not saved." beside Save, form stays open
  let toast =
    res.outcome === OUTCOME.AUTH || res.outcome === OUTCOME.TIMEOUT || res.outcome === OUTCOME.NETWORK
      ? res.text
      : DESIGN_TEXT.generic;
  return { ok: false, fieldErrors: {}, formError: DESIGN_TEXT.notSaved, toast };
};

export default classify;
